import { useState } from "react";
import type { Pedido } from "../types/pedido";
import { fetchDatosReporte } from "../lib/reportes/datos";
import { generarReporteCliente } from "../lib/reportes/reporte-cliente";
import { generarBitacora } from "../lib/reportes/bitacora";
import { generarPDFVistaPrevia, borrarArchivoStorage } from "../lib/storage";

const BANNER_HTML = `
<div style="background:#dc2626;color:#fff;
            text-align:center;padding:10px;font-weight:700;font-family:sans-serif;
            font-size:14px;letter-spacing:0.5px;">
  VISTA PREVIA — DOCUMENTO NO OFICIAL — los datos pueden cambiar antes del cierre del pedido
</div>`;

// Inyecta el banner justo después de <body ...> sin tocar generarReporteCliente/generarBitacora
function inyectarBannerVistaPrevia(html: string): string {
  const match = html.match(/<body[^>]*>/i);
  if (!match) return BANNER_HTML + html;
  const idx = match.index! + match[0].length;
  return html.slice(0, idx) + BANNER_HTML + html.slice(idx);
}

async function descargarPdfDesdeUrl(url: string, nombreArchivo: string): Promise<void> {
  const res = await fetch(url);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

export function useVistaPrevia(pedido: Pedido) {
  const [generandoVistaCliente, setGenerandoVistaCliente] = useState(false);
  const [generandoVistaBitacora, setGenerandoVistaBitacora] = useState(false);

  const generarYDescargar = async (tipo: 'reporte-cliente' | 'bitacora') => {
    const datos = await fetchDatosReporte(pedido.id, { incluirNoAceptadas: true });
    const html = inyectarBannerVistaPrevia(
      tipo === 'reporte-cliente' ? generarReporteCliente(datos) : generarBitacora(datos)
    );
    const { url, path } = await generarPDFVistaPrevia(pedido.id, pedido.numeroPedido, html, tipo);
    const sufijo = tipo === 'reporte-cliente' ? 'reporte-cliente' : 'bitacora';
    await descargarPdfDesdeUrl(url, `vista-previa-${sufijo}-${pedido.numeroPedido}.pdf`);
    borrarArchivoStorage(path); // best-effort, no bloquea la descarga
  };

  const vistaPreviaReporteCliente = async () => {
    if (generandoVistaCliente || generandoVistaBitacora) return; // evita doble clic / disparo simultáneo
    setGenerandoVistaCliente(true);
    try {
      await generarYDescargar('reporte-cliente');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Error en vista previa de reporte cliente:", err);
      alert(`Error al generar vista previa: ${msg}`);
    } finally {
      setGenerandoVistaCliente(false);
    }
  };

  const vistaPreviaBitacora = async () => {
    if (generandoVistaCliente || generandoVistaBitacora) return; // evita doble clic / disparo simultáneo
    setGenerandoVistaBitacora(true);
    try {
      await generarYDescargar('bitacora');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Error en vista previa de bitácora:", err);
      alert(`Error al generar vista previa: ${msg}`);
    } finally {
      setGenerandoVistaBitacora(false);
    }
  };

  return {
    generandoVistaCliente,
    generandoVistaBitacora,
    vistaPreviaReporteCliente,
    vistaPreviaBitacora,
  };
}
